import { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Card } from "@/components/ui/card";
import { Sheet, SheetContent } from "@/components/ui/sheet";
import { useToast } from "@/components/ui/use-toast";
import { appointmentsAPI } from "@/lib/appointments-api";
import { useAuthStore } from "@/stores/authStore";
import { CONSOLE_PERMISSIONS } from "@/constants/permissions";
import type {
  CalendarStaff,
  ServiceCategory,
  ServiceStaff,
} from "@/types/appointments";
import { AppointmentDetailsDialog } from "./AppointmentDetailsDialog";
import { AppointmentsFilterRail } from "./AppointmentsFilterRail";
import { AppointmentsWeekGrid } from "./AppointmentsWeekGrid";
import { AppointmentsWeekToolbar } from "./AppointmentsWeekToolbar";
import type { AppointmentCalendarItem, CalendarView } from "./types";
import {
  DEFAULT_EXPANDED_SECTIONS,
  createTimeSlots,
  formatDayPeriodLabel,
  formatDateForAPI,
  formatWeekPeriodLabel,
  getDayRange,
  getSlotKey,
  getWeekDays,
  getWeekRange,
} from "./utils";

export const AppointmentsCalendarScreen = () => {
  const { toast } = useToast();
  const {
    hasAnyPermission,
    hasPermission,
    isNutritionist,
    isPhysiotherapist,
    getUserRole,
    user,
  } = useAuthStore();
  const navigate = useNavigate();
  const canManageAppointments = hasPermission(
    CONSOLE_PERMISSIONS.MANAGE_APPOINTMENTS
  );
  const isClinicalProvider =
    isNutritionist() ||
    isPhysiotherapist() ||
    getUserRole()?.toLowerCase() === "doctor";
  const canOpenAppointmentList = hasAnyPermission([
    CONSOLE_PERMISSIONS.VIEW_APPOINTMENTS,
  ]);
  const isClinician = isClinicalProvider;

  const [currentDate, setCurrentDate] = useState(new Date());
  const [calendarView, setCalendarView] = useState<CalendarView>("week");
  const [serviceStaffList, setServiceStaffList] = useState<ServiceStaff[]>([]);
  const [serviceCategories, setServiceCategories] = useState<ServiceCategory[]>(
    []
  );
  const [calendarData, setCalendarData] = useState<CalendarStaff[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [hasLoadedOnce, setHasLoadedOnce] = useState(false);
  const [selectedStaff, setSelectedStaff] = useState<Set<string>>(new Set());
  const [selectedServices, setSelectedServices] = useState<Set<string>>(
    new Set()
  );
  const [expandedSections, setExpandedSections] = useState<Set<string>>(
    new Set(DEFAULT_EXPANDED_SECTIONS)
  );
  const [selectedAppointmentId, setSelectedAppointmentId] = useState<
    string | null
  >(null);
  const [appointmentDialogOpen, setAppointmentDialogOpen] = useState(false);
  const [isFilterPanelOpen, setIsFilterPanelOpen] = useState(false);
  const [isDesktopViewport, setIsDesktopViewport] = useState(
    typeof window !== "undefined" ? window.innerWidth >= 1280 : false
  );

  useEffect(() => {
    const updateViewport = () => {
      setIsDesktopViewport(window.innerWidth >= 1280);
    };

    updateViewport();
    window.addEventListener("resize", updateViewport);

    return () => {
      window.removeEventListener("resize", updateViewport);
    };
  }, []);

  const loadCalendarData = useCallback(async () => {
    if (selectedStaff.size === 0 && selectedServices.size === 0) {
      setCalendarData([]);
      setHasLoadedOnce(true);
      return;
    }

    setIsLoading(true);

    try {
      const currentRange =
        calendarView === "day"
          ? getDayRange(currentDate)
          : getWeekRange(currentDate);
      const data = await appointmentsAPI.getCalendar({
        service_ids: Array.from(selectedServices).join(","),
        doctor_ids: Array.from(selectedStaff).join(","),
        start_date: formatDateForAPI(currentRange.start),
        end_date: formatDateForAPI(currentRange.end),
      });

      setCalendarData(data);
    } catch {
      toast({
        title: "Error",
        description: "Failed to load calendar",
        variant: "destructive",
      });
    } finally {
      setIsLoading(false);
      setHasLoadedOnce(true);
    }
  }, [calendarView, currentDate, selectedServices, selectedStaff, toast]);

  useEffect(() => {
    const loadInitialData = async () => {
      try {
        const [staff, services] = await Promise.all([
          appointmentsAPI.getServiceStaff(),
          appointmentsAPI.getServices(),
        ]);

        setServiceStaffList(staff);

        if (isClinicalProvider) {
          const currentStaff = staff.find(
            (s) =>
              s.doctor__id === user?.id || s.doctor__id === user?.zarity_user_id
          );
          const clinicianCategories = currentStaff
            ? services.filter((cat) => cat.name === currentStaff.category)
            : services;
          setServiceCategories(clinicianCategories);
          setSelectedServices(
            new Set(
              clinicianCategories.flatMap((cat) =>
                cat.services.map((s) => s.id)
              )
            )
          );
          setSelectedStaff(
            currentStaff ? new Set([currentStaff.doctor__id]) : new Set()
          );
        } else {
          setServiceCategories(services);
          setSelectedServices(
            new Set(
              services.flatMap((category) =>
                category.services.map((service) => service.id)
              )
            )
          );
          setSelectedStaff(new Set(staff.map((member) => member.doctor__id)));
        }
      } catch {
        toast({
          title: "Error",
          description: "Failed to load appointment data",
          variant: "destructive",
        });
      }
    };

    loadInitialData();
  }, [toast]);

  useEffect(() => {
    loadCalendarData();
  }, [loadCalendarData]);

  const currentRange = useMemo(
    () =>
      calendarView === "day"
        ? getDayRange(currentDate)
        : getWeekRange(currentDate),
    [calendarView, currentDate]
  );
  const visibleDays = useMemo(
    () =>
      calendarView === "day"
        ? [currentRange.start]
        : getWeekDays(currentRange.start),
    [calendarView, currentRange]
  );
  const timeSlots = useMemo(() => createTimeSlots(), []);

  const appointmentsBySlot = useMemo(() => {
    const slotMap = new Map<string, AppointmentCalendarItem[]>();

    calendarData.forEach((staff) => {
      staff.appointments.forEach((appointment) => {
        const start = new Date(appointment.start_time);
        const slotKey = getSlotKey(start, start.getHours());
        const currentItems = slotMap.get(slotKey) ?? [];

        currentItems.push({
          ...appointment,
          staffId: staff.id,
          staffName: staff.full_name,
        });

        currentItems.sort(
          (left, right) =>
            new Date(left.start_time).getTime() -
            new Date(right.start_time).getTime()
        );

        slotMap.set(slotKey, currentItems);
      });
    });

    return slotMap;
  }, [calendarData]);

  const toggleSetValue = (
    value: string,
    currentSet: Set<string>,
    setter: (nextSet: Set<string>) => void
  ) => {
    const nextSet = new Set(currentSet);
    if (nextSet.has(value)) {
      nextSet.delete(value);
    } else {
      nextSet.add(value);
    }
    setter(nextSet);
  };

  const toggleSection = (section: string) => {
    const nextSections = new Set(expandedSections);
    if (nextSections.has(section)) {
      nextSections.delete(section);
    } else {
      nextSections.add(section);
    }

    setExpandedSections(nextSections);
  };

  return (
    <div className="flex h-full min-w-0 gap-3">
      <div className="flex min-h-0 min-w-0 flex-1 flex-col">
        <Card className="flex min-h-0 flex-1 flex-col overflow-hidden">
          <AppointmentsWeekToolbar
            canManageAppointments={canManageAppointments}
            isFilterPanelOpen={isFilterPanelOpen}
            currentPeriodLabel={
              calendarView === "day"
                ? formatDayPeriodLabel(currentRange.start)
                : formatWeekPeriodLabel(currentRange.start, currentRange.end)
            }
            currentView={calendarView}
            onToday={() => setCurrentDate(new Date())}
            onViewChange={setCalendarView}
            onPreviousPeriod={() => {
              const nextDate = new Date(currentDate);
              nextDate.setDate(
                nextDate.getDate() - (calendarView === "day" ? 1 : 7)
              );
              setCurrentDate(nextDate);
            }}
            onNextPeriod={() => {
              const nextDate = new Date(currentDate);
              nextDate.setDate(
                nextDate.getDate() + (calendarView === "day" ? 1 : 7)
              );
              setCurrentDate(nextDate);
            }}
            onToggleFilters={() => setIsFilterPanelOpen((open) => !open)}
            showAppointmentList={canOpenAppointmentList}
            onOpenAppointmentList={() =>
              navigate("/settings/appointments/list")
            }
            onBlockTime={() => navigate("/settings/appointments/blocked-time")}
            onAddAppointment={() =>
              navigate("/settings/appointments/add-appointment")
            }
          />

          <AppointmentsWeekGrid
            isLoading={isLoading}
            hasLoadedOnce={hasLoadedOnce}
            selectedDate={currentDate}
            view={calendarView}
            visibleDays={visibleDays}
            timeSlots={timeSlots}
            appointmentsBySlot={appointmentsBySlot}
            serviceStaffList={serviceStaffList}
            onAppointmentClick={(appointmentId) => {
              setSelectedAppointmentId(appointmentId);
              setAppointmentDialogOpen(true);
            }}
          />
        </Card>
      </div>

      {isDesktopViewport && isFilterPanelOpen ? (
        <aside className="hidden h-full w-80 shrink-0 xl:block">
          <AppointmentsFilterRail
            className="h-full"
            showHeader
            onClose={() => setIsFilterPanelOpen(false)}
            currentDate={currentDate}
            onSelectDate={setCurrentDate}
            onMonthChange={setCurrentDate}
            serviceCategories={serviceCategories}
            serviceStaffList={serviceStaffList}
            selectedServices={selectedServices}
            selectedStaff={selectedStaff}
            showStaffFilter={!isClinicalProvider}
            expandedSections={expandedSections}
            onSectionToggle={toggleSection}
            onServiceToggle={(serviceId) =>
              toggleSetValue(serviceId, selectedServices, setSelectedServices)
            }
            onServiceCategoryToggle={(categoryName) => {
              const categoryServices =
                serviceCategories.find(
                  (category) => category.name === categoryName
                )?.services ?? [];
              const nextSelected = new Set(selectedServices);
              const categoryServiceIds = categoryServices.map(
                (service) => service.id
              );
              const allSelected = categoryServiceIds.every((id) =>
                selectedServices.has(id)
              );

              categoryServiceIds.forEach((id) => {
                if (allSelected) {
                  nextSelected.delete(id);
                } else {
                  nextSelected.add(id);
                }
              });

              setSelectedServices(nextSelected);
            }}
            onStaffToggle={(staffId) =>
              toggleSetValue(staffId, selectedStaff, setSelectedStaff)
            }
            onStaffCategoryToggle={(categoryName) => {
              const categoryStaff = serviceStaffList.filter(
                (staff) => staff.category === categoryName
              );
              const categoryStaffIds = categoryStaff.map(
                (staff) => staff.doctor__id
              );
              const allSelected = categoryStaffIds.every((id) =>
                selectedStaff.has(id)
              );
              const nextSelected = new Set(selectedStaff);

              categoryStaffIds.forEach((id) => {
                if (allSelected) {
                  nextSelected.delete(id);
                } else {
                  nextSelected.add(id);
                }
              });

              setSelectedStaff(nextSelected);
            }}
          />
        </aside>
      ) : null}

      {!isDesktopViewport ? (
        <Sheet open={isFilterPanelOpen} onOpenChange={setIsFilterPanelOpen}>
          <SheetContent
            side="right"
            className="w-full max-w-sm p-0 sm:max-w-sm [&>button]:hidden"
          >
            <AppointmentsFilterRail
              className="h-full rounded-none border-0 shadow-none"
              showHeader
              onClose={() => setIsFilterPanelOpen(false)}
              currentDate={currentDate}
              onSelectDate={setCurrentDate}
              onMonthChange={setCurrentDate}
              serviceCategories={serviceCategories}
              serviceStaffList={serviceStaffList}
              selectedServices={selectedServices}
              selectedStaff={selectedStaff}
              expandedSections={expandedSections}
              onSectionToggle={toggleSection}
              onServiceToggle={(serviceId) =>
                toggleSetValue(serviceId, selectedServices, setSelectedServices)
              }
              onServiceCategoryToggle={(categoryName) => {
                const categoryServices =
                  serviceCategories.find(
                    (category) => category.name === categoryName
                  )?.services ?? [];
                const nextSelected = new Set(selectedServices);
                const categoryServiceIds = categoryServices.map(
                  (service) => service.id
                );
                const allSelected = categoryServiceIds.every((id) =>
                  selectedServices.has(id)
                );

                categoryServiceIds.forEach((id) => {
                  if (allSelected) {
                    nextSelected.delete(id);
                  } else {
                    nextSelected.add(id);
                  }
                });

                setSelectedServices(nextSelected);
              }}
              onStaffToggle={(staffId) =>
                toggleSetValue(staffId, selectedStaff, setSelectedStaff)
              }
              onStaffCategoryToggle={(categoryName) => {
                const categoryStaff = serviceStaffList.filter(
                  (staff) => staff.category === categoryName
                );
                const categoryStaffIds = categoryStaff.map(
                  (staff) => staff.doctor__id
                );
                const allSelected = categoryStaffIds.every((id) =>
                  selectedStaff.has(id)
                );
                const nextSelected = new Set(selectedStaff);

                categoryStaffIds.forEach((id) => {
                  if (allSelected) {
                    nextSelected.delete(id);
                  } else {
                    nextSelected.add(id);
                  }
                });

                setSelectedStaff(nextSelected);
              }}
            />
          </SheetContent>
        </Sheet>
      ) : null}

      <AppointmentDetailsDialog
        appointmentId={selectedAppointmentId}
        open={appointmentDialogOpen}
        onOpenChange={setAppointmentDialogOpen}
        onAppointmentCancelled={loadCalendarData}
        canManageAppointments={canManageAppointments}
        isClinician={isClinician}
      />
    </div>
  );
};
